/** Copy for the focused landing page used by paid campaigns. */
export const getStarted = {
  eyebrow: 'Secure wallet infrastructure',
  title: 'Your next move onchain starts here.',
  description:
    'Manage your treasury, move money, and build digital asset products. One wallet platform, with security built into every transaction.',
  demoTitle: 'See Den in action',
  demoDescription:
    'Tell us what you’re building. We’ll show you how Den can work for your team.',
  benefits: [
    'A walkthrough tailored to your business',
    'A review of your wallet operations and how to strengthen them',
    'Answers to your integration and deployment questions',
  ],
  trust: [
    {
      icon: 'cert',
      title: 'SOC 2 Type II',
      description: 'Independently certified',
    },
    {
      icon: 'verified',
      title: 'Audited contracts',
      description: 'Certora & yAudit',
    },
    {
      icon: 'shield',
      title: 'Multi-layer security',
      description: 'Independent policy checks',
    },
  ],
  useCasesTitle: 'One platform. Your way to work onchain.',
  useCasesDescription:
    'Choose what you want to do. We’ll help you put it into practice.',
  useCases: [
    {
      id: 'treasury',
      label: 'Manage your treasury',
      icon: 'treasury',
      eyebrow: 'Treasury operations',
      title: 'Your assets. Your rules. One clear view.',
      description:
        'Bring wallets, balances, and onchain activity into one place. Put assets to work in DeFi with the permissions and approvals your team needs.',
      points: [
        {
          title: 'See the whole picture',
          description:
            'Monitor balances and activity across supported EVM networks from a single dashboard.',
        },
        {
          title: 'Put your assets to work in DeFi',
          description:
            'Access DeFi protocols directly from your treasury, with permissions for the contracts and functions your team can use.',
        },
        {
          title: 'Make every approval count',
          description:
            'Set who can initiate and approve transactions, with spending limits and allowed destinations.',
        },
        {
          title: 'Keep a clear record',
          description:
            'Follow transactions, approvals, and policy changes with exportable audit logs.',
        },
      ],
    },
    {
      id: 'payments',
      label: 'Move money',
      icon: 'payments',
      eyebrow: 'Payments & onchain operations',
      title: 'Keep payments moving. Keep control.',
      description:
        'Run vendor payments, payroll, and distributions through one governed workflow, from preparation to approval.',
      points: [
        {
          title: 'Prepare payments together',
          description:
            'Build a batch of transfers and review what will move before anyone signs.',
        },
        {
          title: 'Delegate within limits',
          description:
            'Let operators initiate payments while policies govern amounts, recipients, and required approvals.',
        },
        {
          title: 'Automate when you’re ready',
          description:
            'Connect payment workflows to your systems through the API and TypeScript SDK.',
        },
      ],
    },
    {
      id: 'wallets',
      label: 'Build with wallets',
      icon: 'waas',
      eyebrow: 'Wallet infrastructure',
      title: 'Build your product. We’ll bring the wallets.',
      description:
        'Launch custodial and non-custodial wallets on a secure foundation, with transaction controls built in.',
      points: [
        {
          title: 'Choose who controls the wallet',
          description:
            'Launch custodial or non-custodial wallets for your customers through the API and TypeScript SDK.',
        },
        {
          title: 'Put policies behind every transaction',
          description:
            'Configure permissions, approval rules, and transaction limits for your application.',
        },
        {
          title: 'Choose how you deploy',
          description: 'Use Den’s infrastructure or host it yourself.',
        },
      ],
    },
    {
      id: 'protocols',
      label: 'Administer your protocol',
      icon: 'defi',
      eyebrow: 'Protocol & smart contract management',
      title: 'Manage your contracts. Protect your protocol.',
      description:
        'Manage smart contract interactions, admin actions, and protocol changes with clear permissions and approvals.',
      points: [
        {
          title: 'Control access to your contracts',
          description:
            'Define which contract addresses, functions, and parameters each operator can use.',
        },
        {
          title: 'Review every contract call',
          description:
            'Check decoded transaction details and collect the required approvals before execution.',
        },
        {
          title: 'Keep a record of every change',
          description:
            'Follow contract interactions, approvals, and policy changes in one audit trail.',
        },
      ],
    },
  ],
} as const;

export type AdUseCaseId = (typeof getStarted.useCases)[number]['id'];

/** Stable campaign URLs keep the original ad distinct from treasury-specific ads. */
export const adCampaigns = [
  { id: 'general', path: '/get-started', initialUseCase: 'treasury' },
  {
    id: 'treasury',
    path: '/get-started/treasury',
    initialUseCase: 'treasury',
  },
  {
    id: 'payments',
    path: '/get-started/payments',
    initialUseCase: 'payments',
  },
  {
    id: 'wallets',
    path: '/get-started/wallets',
    initialUseCase: 'wallets',
  },
  {
    id: 'protocols',
    path: '/get-started/protocols',
    initialUseCase: 'protocols',
  },
] as const;

export type AdCampaign = (typeof adCampaigns)[number];

export function getAdCampaign(pathname: string): AdCampaign | undefined {
  const path = pathname.replace(/\/+$/, '') || '/';
  return adCampaigns.find((campaign) => campaign.path === path);
}
